import { useEffect, useState } from 'react';
import { themeApi, type Theme } from './api';
import { setSkinLabels } from './labels';

/**
 * Zet een skin om naar CSS-variabelen en data-attributen op <html>.
 * De Swift-app doet straks hetzelfde met dezelfde JSON → één bron voor de look.
 */

const DENSITY = { compact: 0.86, normal: 1, large: 1.18 } as const;

let current: Theme | null = null;
const themeListeners = new Set<(t: Theme) => void>();

export function currentTheme(): Theme | null {
  return current;
}

/** De actieve skin, ook live tijdens het bewerken in de studio. */
export function useCurrentTheme(): Theme | null {
  const [t, setT] = useState(current);
  useEffect(() => {
    themeListeners.add(setT);
    return () => {
      themeListeners.delete(setT);
    };
  }, []);
  return t;
}

export function applyTheme(theme: Theme): void {
  current = theme;
  const root = document.documentElement;
  const { color, font, radius, focus } = theme.tokens;

  // Waarden van de vorige skin weghalen, anders blijven ontbrekende kleuren "hangen"
  for (const prop of [...root.style]) {
    if (prop.startsWith('--c-') || prop.startsWith('--r-') || prop.startsWith('--font-')) root.style.removeProperty(prop);
  }

  for (const [name, value] of Object.entries(color)) {
    root.style.setProperty(`--c-${kebab(name)}`, value);
  }
  for (const [name, value] of Object.entries(radius)) {
    root.style.setProperty(`--r-${kebab(name)}`, `${value}px`);
  }
  if (font.display) root.style.setProperty('--font-display', font.display);
  if (font.body) root.style.setProperty('--font-body', font.body);
  root.style.setProperty('--focus-scale', String(focus?.scale ?? 1.04));
  root.style.setProperty('--ui-scale', String(theme.tokens.scale ?? 1));

  const c = theme.components ?? {};
  root.style.setProperty('--card-scale', String(DENSITY[c.density ?? 'normal'] ?? 1));
  root.dataset.focusStyle = focus?.style ?? 'ring';
  root.dataset.nav = theme.layout?.nav === 'left' ? 'left' : 'top';
  root.dataset.backdrop = c.backdrop ?? 'none';
  root.dataset.logos = c.logos === false ? 'off' : 'on';
  root.dataset.cardTitles = c.cardTitles ?? 'show';
  root.dataset.badges = c.posterBadges === 'never' ? 'never' : 'auto';
  setSkinLabels(c.mediaFlags ?? 'off', c.mediaFlagsPos ?? 'tl');
  root.dataset.hero = c.heroHeight ?? 'normal';
  root.dataset.navStyle = c.navStyle ?? 'pill';
  root.dataset.glass = c.glass ? 'on' : 'off';
  root.dataset.motion = deviceMotion() ?? (c.reduceMotion ? 'reduced' : 'full');
  themeListeners.forEach((l) => l(theme));

  const meta = document.querySelector('meta[name="theme-color"]');
  if (meta && color.background) meta.setAttribute('content', color.background);

  let link = document.getElementById('theme-fonts') as HTMLLinkElement | null;
  if (font.googleFonts) {
    const href = `https://fonts.googleapis.com/css2?${font.googleFonts}&display=swap`;
    if (!link) {
      link = document.createElement('link');
      link.id = 'theme-fonts';
      link.rel = 'stylesheet';
      document.head.appendChild(link);
    }
    if (link.href !== href) link.href = href;
  } else if (link) {
    link.remove();
  }
}

function kebab(s: string): string {
  return s.replace(/[A-Z]/g, (m) => `-${m.toLowerCase()}`);
}

// ---------- Gekozen skin ----------

const KEY = 'tumedia.theme';

/** Laatst gebruikte skin in deze browser (voor het loginscherm en als terugval). */
export function storedThemeId(): string {
  try {
    return localStorage.getItem(KEY) ?? 'tumedia';
  } catch {
    return 'tumedia';
  }
}

export function storeThemeId(id: string): void {
  try {
    localStorage.setItem(KEY, id);
  } catch {
    // privévenster of geblokkeerde opslag: dan geldt de skin alleen deze sessie
  }
}

/** Skin kiezen voor dit profiel: toepassen, onthouden in de browser en op de server (geldt op elk apparaat). */
export async function chooseTheme(theme: Theme): Promise<void> {
  applyTheme(theme);
  storeThemeId(theme.id);
  await themeApi.setChoice(theme.id).catch(() => undefined);
}

/** Na het kiezen van een profiel: de skin van dat profiel laden. */
export async function loadProfileTheme(): Promise<void> {
  const r = await themeApi.choice().catch(() => null);
  const id = r?.themeId;
  if (!id || id === current?.id) return;
  const t = await themeApi.get(id).catch(() => null);
  if (t) {
    applyTheme(t);
    storeThemeId(t.id);
  }
}

/** Opnieuw de opgeslagen skin toepassen (bijv. na het verlaten van de studio zonder opslaan). */
export async function restoreTheme(): Promise<void> {
  const t = await themeApi.get(storedThemeId()).catch(() => themeApi.get('tumedia').catch(() => null));
  if (t) applyTheme(t);
}

// ---------- Titelpagina: klassiek of cinematic ----------

const LAYOUT_KEY = 'tumedia.titleLayout';
const layoutListeners = new Set<() => void>();

function storedLayout(): 'classic' | 'cinematic' | null {
  try {
    const v = localStorage.getItem(LAYOUT_KEY);
    return v === 'classic' || v === 'cinematic' ? v : null;
  } catch {
    return null;
  }
}

/**
 * Indeling van de titelpagina: wat de skin zegt, tenzij je op de pagina zelf hebt gewisseld
 * (dat onthoudt deze browser, zoals de knop bij DexHub).
 */
export function useTitleLayout(): ['classic' | 'cinematic', () => void] {
  const theme = useCurrentTheme();
  const [own, setOwn] = useState(storedLayout);
  useEffect(() => {
    const f = () => setOwn(storedLayout());
    layoutListeners.add(f);
    return () => { layoutListeners.delete(f); };
  }, []);
  const layout = own ?? theme?.components?.titleLayout ?? 'classic';
  const toggle = () => {
    try {
      localStorage.setItem(LAYOUT_KEY, layout === 'cinematic' ? 'classic' : 'cinematic');
    } catch {
      // geen opslag: dan alleen tot de pagina sluit
    }
    setOwn(layout === 'cinematic' ? 'classic' : 'cinematic');
    layoutListeners.forEach((l) => l());
  };
  return [layout, toggle];
}

/** "Kleur volgt de titel": de sfeerkleur als CSS-variabelen (of niets). */
export function titleTintStyle(color: { bg: string; glow: string } | null | undefined, on: boolean): Record<string, string> {
  if (!on || !color) return {};
  return { '--c-background': color.bg, '--title-glow': color.glow };
}

// ---------- Dit apparaat: licht, gemiddeld of sterk ----------

const DEVICE_KEY = 'tumedia.device';
export type DeviceClass = 'light' | 'balanced' | 'strong';

export function deviceClass(): DeviceClass | null {
  try {
    const v = localStorage.getItem(DEVICE_KEY);
    return v === 'light' || v === 'balanced' || v === 'strong' ? v : null;
  } catch {
    return null;
  }
}

/** Een licht apparaat krijgt altijd minder beweging, los van de skin. */
function deviceMotion(): 'reduced' | null {
  return deviceClass() === 'light' ? 'reduced' : null;
}

export function setDeviceClass(v: DeviceClass): void {
  try {
    localStorage.setItem(DEVICE_KEY, v);
  } catch {
    // geen opslag
  }
  if (current) applyTheme(current);
}

/** Voorstel op basis van wat de browser over het apparaat zegt. */
export function suggestDeviceClass(): DeviceClass {
  const n = navigator as Navigator & { deviceMemory?: number };
  const cores = n.hardwareConcurrency ?? 4;
  const mem = n.deviceMemory ?? 4;
  if (cores <= 2 || mem <= 2 || /tizen|webos|smart-?tv|aft[a-z]/i.test(n.userAgent)) return 'light';
  if (cores >= 8 && mem >= 8) return 'strong';
  return 'balanced';
}

/** Zelf gekozen indeling van de titelpagina (Snel instellen). */
export function setTitleLayout(v: 'classic' | 'cinematic'): void {
  try {
    localStorage.setItem(LAYOUT_KEY, v);
  } catch {
    // geen opslag
  }
  layoutListeners.forEach((l) => l());
}
